import { ShopperApi, shopperApi } from '../lib/shopperApi';
import { useAuthStore } from './authStore';
import { useCartStore } from './cartStore';
import { MAX_WISHLIST_MERGE, useWishlistStore } from './wishlistStore';

/** The backend accepts at most this many lines in one cart merge. */
const MAX_CART_MERGE = 50;

/** Move this device's bag and hearts into the account, then show what the account holds. */
export async function mergeIntoAccount(api: ShopperApi = shopperApi): Promise<void> {
  const lines = useCartStore
    .getState()
    .items.slice(0, MAX_CART_MERGE)
    .map((i) => ({ item_id: i.inventory_id, quantity: i.quantity }));
  const ids = useWishlistStore.getState().ids.slice(0, MAX_WISHLIST_MERGE);
  const [cart, wishlist] = await Promise.allSettled([
    lines.length ? api.mergeCart(lines) : api.cart(),
    ids.length ? api.mergeWishlist(ids) : api.wishlist(),
  ]);
  if (cart.status === 'fulfilled') useCartStore.getState().hydrate(cart.value);
  else useCartStore.setState({ syncError: 'Could not load your saved bag.' });
  if (wishlist.status === 'fulfilled') useWishlistStore.getState().hydrate(wishlist.value);
  else useWishlistStore.setState({ error: 'Could not load your wishlist.' });
}

/**
 * Keeps the bag and wishlist with the account: merged in on sign-in (or on load
 * while signed in), forgotten on this device on sign-out. Returns the unsubscribe.
 */
export function startAccountSync(api: ShopperApi = shopperApi): () => void {
  return useAuthStore.subscribe((state, previous) => {
    if (state.status === previous.status) return;
    if (state.status === 'authed') void mergeIntoAccount(api);
    // Also when a saved session expired between visits: that bag belongs to the account.
    else if (state.status === 'guest' && (previous.status === 'authed' || useCartStore.getState().synced)) {
      useCartStore.getState().reset();
      useWishlistStore.getState().reset();
    }
  });
}
